import { AlertStatus } from '@/entities/alert-status';
import {
  type TimeseriesLine,
  type TimeseriesMetric,
} from '@/pcs/domain/entities/pcs-timeseries';
import { AlertStatusFromValue } from '@/pcs/domain/entities/score';

export interface PCSPowerEfficiencyDCHRow {
  'mantle_pcs_status.accure_id': string;
  'frontend_id_mapping.full_frontend_id': string;
  'mantle_pcs_status.pcs_efficiency_score': number;
  'mantle_pcs_timeseries.window_end_date': Date;
  'mantle_pcs_timeseries.pcs_efficiency_dch_avg': number;
  'mantle_pcs_timeseries.pcs_efficiency_dch_min': number;
  'mantle_pcs_timeseries.pcs_efficiency_dch_max': number;
}

export interface PCSPowerEfficiencyCHARow {
  'mantle_pcs_status.accure_id': string;
  'frontend_id_mapping.full_frontend_id': string;
  'mantle_pcs_status.pcs_efficiency_score': number;
  'mantle_pcs_timeseries.window_end_date': Date;
  'mantle_pcs_timeseries.pcs_efficiency_cha_avg': number;
  'mantle_pcs_timeseries.pcs_efficiency_cha_min': number;
  'mantle_pcs_timeseries.pcs_efficiency_cha_max': number;
}

export interface PCSPowerEfficiencyRow {
  id: string;
  full_frontend_id: string;
  pcs_efficiency_score: number;
  window_end_date: Date;
  pcs_efficiency_avg: number;
  pcs_efficiency_min: number;
  pcs_efficiency_max: number;
}

type LinesMapType = Record<string, TimeseriesLine>;

export function transformPowerEfficiencyDCHRows(
  rows: PCSPowerEfficiencyRow[],
): TimeseriesMetric {
  const [linesMap, metricStatus] = transformPowerEfficiencyRowsCore(rows);

  // 3) Return the final TimeseriesMetric
  const metric: TimeseriesMetric = {
    metricName: 'PCS Efficiency (Discharge)',
    metricStatus,
    lines: Object.values(linesMap),
  };

  return metric;
}

export function transformPowerEfficiencyCHARows(
  rows: PCSPowerEfficiencyRow[],
): TimeseriesMetric {
  const [linesMap, metricStatus] = transformPowerEfficiencyRowsCore(rows);

  // 3) Return the final TimeseriesMetric
  const metric: TimeseriesMetric = {
    metricName: 'PCS Efficiency (Charge)',
    metricStatus,
    lines: Object.values(linesMap),
  };

  return metric;
}

export function transformPowerEfficiencyRowsCore(
  rows: PCSPowerEfficiencyRow[],
): [LinesMapType, AlertStatus] {
  // 1) Create a map from ID => TimeseriesLine
  const linesMap: LinesMapType = {};

  for (const row of rows) {
    const id = row['id'];
    const fullFrontendID = row['full_frontend_id'];
    if (!id) continue;

    // Ensure there's a line for this ID
    if (!linesMap[id]) {
      linesMap[id] = {
        id,
        fullFrontendID,
        idStatus: AlertStatusFromValue[row['pcs_efficiency_score']],
        subLines: [
          { label: 'avg', data: [] },
          { label: 'min', data: [] },
          { label: 'max', data: [] },
        ],
      };
    }

    const timestamp = new Date(row['window_end_date']);

    // Push each measure into its subLine
    linesMap[id].subLines
      .find((s) => s.label === 'avg')
      ?.data.push({
        timestamp,
        value: row['pcs_efficiency_avg'],
      });
    linesMap[id].subLines
      .find((s) => s.label === 'min')
      ?.data.push({
        timestamp,
        value: row['pcs_efficiency_min'],
      });
    linesMap[id].subLines
      .find((s) => s.label === 'max')
      ?.data.push({
        timestamp,
        value: row['pcs_efficiency_max'],
      });
  }

  // 2) Derive an overall metricStatus if desired
  let metricStatus: AlertStatus = AlertStatus.OK;
  for (const line of Object.values(linesMap)) {
    if (line.idStatus === AlertStatus.CRITICAL) {
      metricStatus = AlertStatus.CRITICAL;
      break; // No need to check further
    } else if (
      line.idStatus === AlertStatus.WARNING &&
      metricStatus !== AlertStatus.WARNING
    ) {
      metricStatus = AlertStatus.WARNING;
      // keep checking in case there's CRITICAL down the line
    } else if (
      line.idStatus === AlertStatus.INFO &&
      metricStatus !== AlertStatus.INFO
    ) {
      metricStatus = AlertStatus.INFO;
      // keep checking in case there's CRITICAL down the line
    }
  }

  return [linesMap, metricStatus];
}
